// Earlobe anchor estimation — pose-gated rigid calibration.
//
// MediaPipe's 468-point mesh has no ear geometry: landmarks 93/323 sit on the
// face-oval silhouette (canonical z = -2.43), while the real earlobe hangs
// below AND roughly 1.1 cm posterior to that point. A depth error is invisible
// frontally but leaks into screen position by dz*sin(yaw) as the head turns,
// which is why a small zDelta looks fine head-on and slides onto the cheek
// under yaw. The default zDelta here accounts for the true ear depth.
//
// Silhouette landmarks are also MediaPipe's noisiest, and the estimator's
// Procrustes fit is weighted toward interior landmarks (eyes/nose), so under
// yaw/pitch the ear-edge points drift in canonical space. The earlobe, however,
// is rigid to the skull. So:
//
//   1. Measure the ear reference as canonical tragus + mean displacement of
//      four ear-edge landmarks from their canonical positions (robust to
//      single-landmark noise).
//   2. Only fold measurements into an EMA-calibrated reference while the face
//      is near-frontal (faceMatrix[10] = cosine between face-forward and the
//      camera axis). When the head is rotated, hold the calibrated point
//      frozen in canonical space and let faceMatrix rotate it rigidly,
//      exactly like a real ear.
//   3. Apply an anatomical offset: lateral (outward), down to the lobe, and
//      posterior. All three are tunable per SKU via manifest.json
//      (earlobeXOffset, earlobeYOffset, earlobeZDelta). 1 unit = 1 cm.

// Canonical positions (cm) of the ear-region silhouette landmarks in the
// MediaPipe canonical face model — the same space as live metricLandmarks
// after Procrustes alignment.
const CANONICAL = {
  93:  [-7.542, -1.049, -2.431],
  132: [-7.271, -2.891, -2.252],
  137: [-7.159, -0.812, -0.072],
  177: [-6.954, -2.440, -0.131],
  323: [7.542, -1.049, -2.431],
  361: [7.271, -2.891, -2.252],
  366: [7.159, -0.812, -0.072],
  401: [6.954, -2.440, -0.131],
};

const REF_INDICES = {
  left:  [93, 132, 137, 177],
  right: [323, 361, 366, 401],
};

const TRAGUS_INDEX = { left: 93, right: 323 };

// Calibration gate: full trust inside 12 deg of frontal, none beyond 25 deg.
const FRONTAL_FULL = Math.cos((12 * Math.PI) / 180);
const FRONTAL_NONE = Math.cos((25 * Math.PI) / 180);
const CALIB_ALPHA = 0.08; // per-frame EMA rate when fully frontal (~1s @30fps)

export const EARLOBE_DEFAULTS = {
  xOffset: 0.2,  // outward, past the mesh silhouette to the lobe surface
  yOffset: 1.2,  // below the tragus point; mid-earlobe / typical piercing
  zDelta:  1.15, // posterior; the pose-critical ear depth term
};

// Returns a computeFn for MindARThree.addDynamicAnchor. Stateful per side.
export const makeEarlobeCompute = (side, { xOffset, yOffset, zDelta }) => {
  const refIdxs = REF_INDICES[side];
  const tragus = CANONICAL[TRAGUS_INDEX[side]];
  const outward = side === 'left' ? -1 : 1;

  const calibrated = [0, 0, 0];
  let frontalFrames = 0;

  return (lm, estimateResult) => {
    // Robust measured tragus: canonical tragus + mean displacement of the
    // ear-edge landmark group from its canonical position.
    let dx = 0, dy = 0, dz = 0;
    for (const i of refIdxs) {
      dx += lm[i][0] - CANONICAL[i][0];
      dy += lm[i][1] - CANONICAL[i][1];
      dz += lm[i][2] - CANONICAL[i][2];
    }
    const n = refIdxs.length;
    const mx = tragus[0] + dx / n;
    const my = tragus[1] + dy / n;
    const mz = tragus[2] + dz / n;

    const frontality = estimateResult?.faceMatrix?.[10] ?? 0;
    // Smoothstep between the no-trust and full-trust cones
    const t = Math.max(0, Math.min(1, (frontality - FRONTAL_NONE) / (FRONTAL_FULL - FRONTAL_NONE)));
    const gate = t * t * (3 - 2 * t);

    if (frontalFrames === 0) {
      // No trustworthy sample yet — follow the live measurement.
      calibrated[0] = mx;
      calibrated[1] = my;
      calibrated[2] = mz;
      if (gate > 0.5) frontalFrames = 1;
    } else if (gate > 0) {
      const a = CALIB_ALPHA * gate;
      calibrated[0] += (mx - calibrated[0]) * a;
      calibrated[1] += (my - calibrated[1]) * a;
      calibrated[2] += (mz - calibrated[2]) * a;
    }
    // else: head rotated — hold the rigid calibrated point.

    return [
      calibrated[0] + outward * xOffset,
      calibrated[1] - yOffset,
      calibrated[2] - zDelta,
    ];
  };
};
